import Phaser from "phaser";
import {
  TICK_TIME,
  PLAYER_TICK_TIME,
  PIXEL_NAME,
  DIRS,
  SCALE,
  CLASS_NAMES,
} from "../../utils/constants";
import {
  Player,
  Boulder,
  Wall,
  Empty,
  Ground,
  Barrel,
  Enemy,
  Interactive,
  Jewel,
  Mushroom,
  NPCharacter,
  Exit,
  Torch,
  Explosion,
  Block,
} from "../objects";
import Character from "../objects/Character";
import Debris from "../objects/Debris";
import Fallable from "../objects/Fallable";
import Pillar from "../objects/Pillar";
import UI from "../UI";
import dbManager from "../../utils/dbManager";

class Level {
  constructor(config) {
    this.config = config;
    this.moveables = [];
    this.parallelMovables = []; // used for gravity loop on falling objects until they rest
    this.objects = [];
    this.interactiveElements = {};
    this.decorationElements = {};
    this.scene = null;
    this.player = null;
    this.playerTimeoutId = 0;
    this.timeoutIds = [];
    this.focusObject = null; // what camera focus on
    this.isPaused = false;
    this.queuedActions = [];
    // stats
    this.countDiamonds = 0;
    this.collectedDiamonds = 0;
    this.countEnemies = 0;
    this.killedEnemies = 0;
    this.countShrooms = 0;
    this.collectedShrooms = 0;
    this.totalShroomsAmount = 0;
    this.prevBest = 0;
    // flags
    this.preventDisplayDiamondsGoalAmount = false;
    // unfinish flag
    this.unfinished = true;
    // loop identifier
    this.tickId = 0;
  }

  onNextTick(listener) {
    this.queuedActions.push(listener);
  }
  runScheduledActions() {
    const toRun = this.queuedActions;
    this.queuedActions = [];
    toRun.forEach((cb) => cb(this));
  }

  async connectToScene(phaserScene) {
    this.scene = phaserScene;
    const layer = this.scene.children.getByName("layer_1");
    const { tilemap: map } = layer;
    if (this.objects.length) {
      this.objects = [];
    }

    this.break_effect = this.scene.add.sprite(48, 16).setOrigin(0, 0.25);
    this.break_effect.setScale(layer.scaleX);
    this.break_effect.depth = 4;

    this.pick_effect = this.scene.add.sprite(48, 16).setOrigin(0, 0.25);
    this.pick_effect.setScale(layer.scaleX);
    this.pick_effect.depth = 4;

    this.enemy_die_effect = this.scene.add.sprite(48, 16).setOrigin(0, 0.25);
    this.enemy_die_effect.setScale(layer.scaleX);
    this.enemy_die_effect.depth = 4;

    const dbData = await dbManager.get(this.constructor.key);
    const shroomAlreadyCollected = dbData && dbData.collectedShrooms !== 0;
    const alreadyCollectedShrooms = dbData?.collectedShrooms || 0;

    let intOrder = 0;
    this.objects = this.config.map((r, y) =>
      r.map((c, x) => {
        // factory
        let element;
        switch (c) {
          case PIXEL_NAME.WALL:
            element = new Wall(c, this);
            element.setPosition({ x, y });
            break;
          case PIXEL_NAME.BLOCK:
            // invisible wall used to avoid player to get positions
            element = new Block(c, this);
            element.setPosition({ x, y });
            break;
          case PIXEL_NAME.TORCH:
            element = new Torch(c, this);
            element.setPosition({ x, y });
            element.order = Object.values(this.decorationElements).filter(
              (el) => el.isClass(Torch)
            ).length;
            this.decorationElements[
              `${element.position.x}-${element.position.y}`
            ] = element;
            break;
          case PIXEL_NAME.DEBRIS:
            element = new Debris(c, this);
            element.setPosition({ x, y });
            break;
          case PIXEL_NAME.GROUND:
            element = new Ground(c, this);
            element.setPosition({ x, y });
            break;
          case PIXEL_NAME.BOULDER:
            element = this.createBoulderAt({ x, y });

            break;
          case PIXEL_NAME.PILLAR:
            const pillar = this.scene.add
              .sprite(48, 16)
              .setOrigin(0.05, 0.2)
              .play("pillar_idle");
            pillar.setScale(layer.scaleX);
            pillar.x = map.tileToWorldX(x);
            pillar.y = map.tileToWorldY(y);

            element = new Pillar(c, this);
            element.alien = pillar;
            break;
          case PIXEL_NAME.JEWEL:
            const diamond = this.scene.add
              .sprite(48, 16)
              .setOrigin(0.05, 0.2)
              .play("diamond_idle");
            diamond.setScale(layer.scaleX);
            diamond.x = map.tileToWorldX(x);
            diamond.y = map.tileToWorldY(y);

            this.countDiamonds++;

            element = new Jewel(c, this);
            element.order = this.countDiamonds;
            element.alien = diamond;
            element.move({ x, y });
            break;
          case PIXEL_NAME.MUSHROOM:
            if (shroomAlreadyCollected) {
              this.collectedShrooms = alreadyCollectedShrooms;
              element = new Empty(c, this);
            } else {
              const mushroom = this.scene.add
                .sprite(48, 16)
                .setOrigin(0, 0)
                .play("mushroom_idle");
              mushroom.setScale(layer.scaleX);
              mushroom.x = map.tileToWorldX(x);
              mushroom.y = map.tileToWorldY(y);

              element = new Mushroom(c, this);
              element.alien = mushroom;
            }
            this.countShrooms++;
            break;
          case PIXEL_NAME.BARREL:
            element = this.createBarrelAt({ x, y });
            break;
          case PIXEL_NAME.PLAYER:
            const player = this.scene.add.sprite(48, 16).setOrigin(0, 0.25);
            player.setScale(layer.scaleX);
            player.x = map.tileToWorldX(x);
            player.y = map.tileToWorldY(y) + 5;

            player.name = "brave";
            player.on(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
              if (!player.isMoving && !player.inactive) {
                player.play("brave_idle");
              }
            });

            element = new Player(c, this);
            element.alien = player;
            element.animIdle();

            // add shortcut reference
            this.player = element;
            break;
          case PIXEL_NAME.BRO:
            const follower = this.scene.add.sprite(48, 16).setOrigin(0, 0.25);
            follower.setScale(layer.scaleX);
            follower.x = map.tileToWorldX(x);
            follower.y = map.tileToWorldY(y) + 5;

            follower.name = "bro";
            follower.on(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
              if (!follower.isMoving && follower.isFollowing) {
                follower.play("bro_idle");
              }
            });

            element = new NPCharacter(c, this, "bro");
            element.alien = follower;
            element.isFollowing = true;
            element.animIdle();

            // add shortcut reference
            this.follower = element;
            break;
          case PIXEL_NAME.DWARF:
            const leader = this.scene.add.sprite(48, 16).setOrigin(0, 0.25);
            leader.setScale(layer.scaleX);
            leader.x = map.tileToWorldX(x);
            leader.y = map.tileToWorldY(y);

            leader.name = "dwarf";
            leader.on(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
              if (!leader.isMoving) {
                leader.play("dwarf_idle");
              }
            });

            element = new NPCharacter(c, this, "dwarf");
            element.alien = leader;
            // element.isFollowing = true
            element.isLeading = true;
            element.animIdle();

            // add shortcut reference
            this.leader = element;
            break;
          case PIXEL_NAME.ENEMY:
            element = this.createEnemyAt({ x, y });
            // const enemy = this.scene.add
            //   .sprite(48, 16)
            //   .setOrigin(0.125, 0.125)
            //   .play("bat_move");
            // enemy.setScale(layer.scaleX);
            // enemy.x = map.tileToWorldX(x);
            // enemy.y = map.tileToWorldY(y);

            this.countEnemies++;

            // element = new Enemy(c, this);
            // element.alien = enemy;
            break;

          case PIXEL_NAME.EXIT:
          case PIXEL_NAME.INTERACTION:
            const intElement =
              c === PIXEL_NAME.EXIT
                ? new Exit(c, this)
                : new Interactive(c, this);
            intElement.order = intOrder++;
            this.interactiveElements[`${x}-${y}`] = intElement;
            intElement.setPosition({ x, y });

            // keep old hook when interactive element is added
            this.onAddElement(intElement);
            // instead of interactive element, add an empty element to the current position
            element = new Empty(c, this);

            if (intElement.isClass(Exit)) {
              this.exit = intElement;
            }
            break;
          default:
            element = new Empty(c, this);
        }
        element.setPosition({ x, y });
        if (!this.objectIsEmpty(element) && !element.isRigid) {
          this.moveables.push(element);
        }
        this.onAddElement(element);
        if (!element.isClass(Empty)) {
          element.addedToScene();
        }
        return element;
      })
    );
    this.focusObject = this.player.alien;

    // unfinish flag

    this.unfinished = !dbData || (dbData && dbData.comp === 0);
    console.log(this.unfinished);

    if (this.diamondsGoal > 0) {
      // draw exit block only on unfinished levels
      if (this.unfinished) {
        const block = this.scene.add
          .sprite(48, 16)
          .setOrigin(0, 0.25)
          .play("exit_block");
        block.setScale(layer.scaleX);
        block.x = map.tileToWorldX(this.exit.position.x - 2);
        block.y = map.tileToWorldY(this.exit.position.y);

        const exitBlock = new Wall(PIXEL_NAME.WALL, this);
        exitBlock.setPosition({
          x: this.exit.position.x - 2,
          y: this.exit.position.y,
        });
        exitBlock.alien = block;
        this.objects[this.exit.position.y][this.exit.position.x - 2] =
          exitBlock;
        this.exit.block = exitBlock;
      }

      // only level 6 needs to have diamonds goal but do not display it until a dialog happens
      if (!this.preventDisplayDiamondsGoalAmount) {
        setTimeout(() => {
          this.scene.events.emit("score:diamond_goal_set", this.diamondsGoal);
        }, 1500);
      }
    }

    // awaits for child level object set goals
    this.onNextTick(async () => {
      if (!(await dbManager.get(this.constructor.key))) {
        await dbManager.set(this.constructor.key, {
          countDiamonds: this.countDiamonds,
          collectedDiamonds: this.collectedDiamonds,
          countEnemies: this.countEnemies,
          killedEnemies: this.killedEnemies,
          countShrooms: this.countShrooms,
          collectedShrooms: this.collectedShrooms,
          comp: 0,
          prevBest: this.prevBest,
        });
      }
    });

    // this.totalShroomsAmount = dbManager.getLevels().reduce((r, l) => r + l.collectedShrooms, 0);
    this.totalShroomsAmount = (await dbManager.get("totalShroomsAmount")) || 0;
  }

  jumpIntoParallelGravity(el) {
    const idx = this.moveables.findIndex((e) => e === el);
    this.moveables.splice(idx, 1);
    el.inParallelGravity = true;
    this.parallelMovables.push(el);
  }
  goBackToMainGravity(el) {
    // no way back when incative (like after explosion)
    if (el.inactive) return false;

    const idx = this.parallelMovables.findIndex((e) => e === el);
    // nothing to move from parallel movables
    if (idx === -1) return false;

    this.parallelMovables.splice(idx, 1);
    el.inParallelGravity = false;
    this.moveables.push(el);
  }
  explodeLoop(barrels) {
    barrels.forEach((el) => {
      const canFallDown = el.canFallDown();
      const objBelow = this.objects[el.position.y + 1][el.position.x];
      const hitUncrushableWhenFalling =
        !canFallDown &&
        el.isFalling &&
        !objBelow.isClass(CLASS_NAMES.EXPLOSION);
      if (hitUncrushableWhenFalling) {
        el.explode();
      }
    });
  }

  // explosionsRemove(explosions) {
  //   explosions.forEach(el => {
  //     this.objects[el.position.y][el.position.x] = new Empty(PIXEL_NAME.EMPTY, this);
  //   })
  // }
  gravityLoop(parallelWorld) {
    const moveables = parallelWorld || this.moveables;
    if (this.isPaused) {
      return;
    }

    if (!parallelWorld) {
      this.runScheduledActions();
    }

    // this.explosionsRemove(moveables.filter((o) => o.isClass(CLASS_NAMES.EXPLOSION) && o.extinguished))
    this.explodeLoop(moveables.filter((o) => o.isClass(CLASS_NAMES.BARREL)));
    moveables
      .sort((a, b) => a.position.y - b.position.y)
      .filter((el) => !el.freeze && !el.exploded)
      .forEach((el) => {
        if (el.freeze) {
          return false;
        }
        // if (el.beingPusehd) {
        //   el.beingPusehd = false
        //   return
        // }
        const canFallDown = el.canFallDown();
        const canFallLeft = el.canFallLeft();
        const canFallRight = el.canFallRight();
        let nextPos;
        if (canFallDown) {
          nextPos = { x: el.position.x, y: el.position.y + 1 };
        }
        if (!canFallDown && canFallLeft) {
          nextPos = { x: el.position.x - 1, y: el.position.y };
          if (el.isFalling) {
            nextPos.y++;
          }
        }
        if (!canFallDown && !canFallLeft && canFallRight) {
          nextPos = { x: el.position.x + 1, y: el.position.y };
          if (el.isFalling) {
            nextPos.y++;
          }
        }

        if (nextPos) {
          this.moveObject(el, nextPos);

          // hit a solid
          if (
            el.isFalling &&
            (canFallLeft || canFallRight) &&
            el.isClass(Jewel)
          ) {
            this.scene.events.emit("sounds:gem_hit");
          }
          if (
            el.isFalling &&
            (canFallLeft || canFallRight) &&
            (el.isClass(Boulder) || el.isClass(Pillar))
          ) {
            this.scene.events.emit("sounds:rock_hit_hard");
          }

          el.isFalling++;
        } else {
          // inform object bellow that something has hit it
          const objectBelow = this.getObjectAt(
            el.position.x,
            el.position.y + 1
          );
          if (el.isFalling && objectBelow) {
            // console.log(el, 'HIT AGAINST', objectBelow);
            objectBelow.hitBy(el);
          }

          if (el.isFalling && (el.isClass(Boulder) || el.isClass(Pillar))) {
            if (objectBelow && objectBelow.isClass(Ground)) {
              this.scene.events.emit("sounds:rock_hit_soft");
            } else {
              this.scene.events.emit("sounds:rock_hit_hard");
            }
          }
          if (el.isFalling && el.isClass(Jewel)) {
            this.scene.events.emit("sounds:gem_hit");
          }
          // if (el.isFalling && el.isClass(Barrel)) {
          //   el.explode();
          // }

          el.isFalling = 0;
        }

        if (!el.isFalling && el.inParallelGravity) {
          this.goBackToMainGravity(el);
        }
      });

    if (!parallelWorld) {
      this.enemiesLoop();
      // this.runScheduledActions();
    }
  }
  enemiesLoop() {
    if (this.isPaused) {
      return;
    }
    let nextPos,
      neighbour,
      isDestinationEmpty,
      isPerpToAnchor,
      newAnchor,
      nextDir,
      backToTrackDir,
      nextAnchorPos;

    const helperDirCalc = (
      enemy,
      nextPos,
      nextDir,
      backToTrackDir,
      nextAnchorPos
    ) => {
      neighbour = this.getObjectAt(nextPos.x, nextPos.y);
      isDestinationEmpty =
        this.objectIsEmpty(neighbour) ||
        (neighbour && neighbour.isClass(Player)); // this.isDestinationEmpty(nextPos.x, nextPos.y)
      // if (!isDestinationEmpty && !enemy.anchorPosition) {
      //   enemy.anchorPosition = neighbour.position
      //   enemy.moveIntentDir = nextDir
      // }
      if (isDestinationEmpty && neighbour && neighbour.isClass(Player)) {
        this.moveObject(enemy, nextPos);
      }
      if (isDestinationEmpty) {
        if (!enemy.anchorPosition) {
          enemy.moveIntentDir = nextDir;
          return;
        }
        isPerpToAnchor =
          enemy.position.x === enemy.anchorPosition.x ||
          enemy.position.y === enemy.anchorPosition.y;
        // scenario 1: enemy is perpendicular to anchor
        if (isPerpToAnchor) {
          // updates anchor counter clockwise
          newAnchor = this.getObjectAt(nextAnchorPos.x, nextAnchorPos.y);
          if (
            !this.objectIsEmpty(newAnchor) &&
            !newAnchor.isFalling &&
            !newAnchor.isClass(Player)
          ) {
            enemy.anchorPosition = newAnchor.position;
          } else {
            enemy.moveIntentDir = backToTrackDir;
          }
        }
        this.moveObject(enemy, nextPos);
      } else {
        enemy.anchorPosition = !neighbour.isFalling ? neighbour.position : null;
        enemy.moveIntentDir = nextDir;
      }
    };
    this.moveables
      .filter((el) => el.isClass(Enemy))
      .forEach((enemy) => {
        // potential path finding mechanism @see https://github.com/qiao/PathFinding.js
        // if(!enemy.path) {
        //   const grid = new window.PF.Grid(this.objects.map(row => row.map(el => el.isEmpty ? 0 : 1)));
        //   enemy.pathfinder = new window.PF.AStarFinder();
        //   const path = enemy.pathfinder.findPath(enemy.position.x, enemy.position.y, thi.player.position.x, this.player.position.y, grid);
        // }
        switch (enemy.moveIntentDir) {
          case DIRS.UP:
            nextPos = { x: enemy.position.x, y: enemy.position.y - 1 };
            nextDir = enemy.scanDirection === "CW" ? DIRS.RIGHT : DIRS.LEFT;
            backToTrackDir =
              enemy.scanDirection === "CW" ? DIRS.LEFT : DIRS.RIGHT;
            nextAnchorPos =
              enemy.scanDirection === "CW"
                ? { x: nextPos.x - 1, y: nextPos.y }
                : { x: nextPos.x + 1, y: nextPos.y };
            // nextAnchorPos = { x: nextPos.x + 1, y: nextPos.y }
            helperDirCalc(
              enemy,
              nextPos,
              nextDir,
              backToTrackDir,
              nextAnchorPos
            );

            break;
          case DIRS.RIGHT:
            nextPos = { x: enemy.position.x + 1, y: enemy.position.y };
            nextDir = enemy.scanDirection === "CW" ? DIRS.DOWN : DIRS.UP;
            backToTrackDir = enemy.scanDirection === "CW" ? DIRS.UP : DIRS.DOWN;
            // nextDir = DIRS.UP
            // backToTrackDir = DIRS.DOWN
            nextAnchorPos =
              enemy.scanDirection === "CW"
                ? { x: nextPos.x, y: nextPos.y - 1 }
                : { x: nextPos.x, y: nextPos.y + 1 };
            // nextAnchorPos = { x: nextPos.x, y: nextPos.y + 1 }
            helperDirCalc(
              enemy,
              nextPos,
              nextDir,
              backToTrackDir,
              nextAnchorPos
            );

            break;
          case DIRS.DOWN:
            nextPos = { x: enemy.position.x, y: enemy.position.y + 1 };
            nextDir = enemy.scanDirection === "CW" ? DIRS.LEFT : DIRS.RIGHT;
            backToTrackDir =
              enemy.scanDirection === "CW" ? DIRS.RIGHT : DIRS.LEFT;
            // nextDir = DIRS.RIGHT
            // backToTrackDir = DIRS.LEFT
            nextAnchorPos =
              enemy.scanDirection === "CW"
                ? { x: nextPos.x + 1, y: nextPos.y }
                : { x: nextPos.x - 1, y: nextPos.y };
            // nextAnchorPos = { x: nextPos.x - 1, y: nextPos.y }
            helperDirCalc(
              enemy,
              nextPos,
              nextDir,
              backToTrackDir,
              nextAnchorPos
            );

            break;
          case DIRS.LEFT:
            nextPos = { x: enemy.position.x - 1, y: enemy.position.y };
            nextDir = enemy.scanDirection === "CW" ? DIRS.UP : DIRS.DOWN;
            backToTrackDir = enemy.scanDirection === "CW" ? DIRS.DOWN : DIRS.UP;
            // nextDir = DIRS.DOWN
            // backToTrackDir = DIRS.UP
            nextAnchorPos =
              enemy.scanDirection === "CW"
                ? { x: nextPos.x, y: nextPos.y + 1 }
                : { x: nextPos.x, y: nextPos.y - 1 };
            // nextAnchorPos = { x: nextPos.x, y: nextPos.y - 1 }
            helperDirCalc(
              enemy,
              nextPos,
              nextDir,
              backToTrackDir,
              nextAnchorPos
            );

            break;

          default:
            break;
        }
        enemy.loop();
      });
  }
  playerLoop() {
    if (this.isPaused || this.player.inactive) {
      return;
    }
    clearTimeout(this.playerTimeoutId);
    const player = this.player;
    const follower = this.follower;
    const leader = this.leader;

    const nextPos = { x: player.position.x, y: player.position.y };
    switch (player.moveIntentDir) {
      case DIRS.UP:
        if (player.canMoveUp()) {
          nextPos.y -= 1;
        }
        break;
      case DIRS.DOWN:
        if (player.canMoveDown()) {
          nextPos.y += 1;
        }
        break;
      case DIRS.LEFT:
        if (
          player.canMoveLeft() ||
          (player.canPushLeft() && player.pushLeft())
        ) {
          nextPos.x -= 1;
        }
        break;
      case DIRS.RIGHT:
        if (
          player.canMoveRight() ||
          (player.canPushRight() && player.pushRight())
        ) {
          nextPos.x += 1;
        }
        break;
      default:
        break;
    }
    // if ((player.position.x !== nextPos.x || player.position.y !== nextPos.y)) {
    if (
      (player.position.x !== nextPos.x || player.position.y !== nextPos.y) &&
      !player.isMoving
    ) {
      player.isMoving = true;
      if (player.position.x < nextPos.x) {
        player.animRunRight();
        // player.animRunLeft();
      }
      if (player.position.x > nextPos.x) {
        player.animRunLeft();
        // player.animRunRight();
      }

      // const isSwitch = nextPos.x === follower.position.x && nextPos.y === follower.position.y

      this.scene.events.emit("player_moves", nextPos);
      // save last player position
      const followerNextPos = player.position;
      // move player
      this.moveObject(player, nextPos);
      // updates follower
      if (follower && follower.isFollowing) {
        follower.moveIntentDir =
          follower.position.y < followerNextPos.y
            ? DIRS.DOWN
            : follower.moveIntentDir;
        follower.moveIntentDir =
          follower.position.y > followerNextPos.y
            ? DIRS.UP
            : follower.moveIntentDir;
        if (follower.position.x < followerNextPos.x) {
          follower.moveIntentDir = DIRS.RIGHT;
          follower.animRunRight();
        }
        if (follower.position.x > followerNextPos.x) {
          follower.moveIntentDir = DIRS.LEFT;
          follower.animRunLeft();
        }

        this.moveObject(follower, followerNextPos);

        if (follower.position.y < player.position.y) {
          follower.alien.depth = 0;
          player.alien.depth = 2;
        } else {
          player.alien.depth = 0;
          follower.alien.depth = 2;
        }
      }
    }

    this.playerTimeoutId = setTimeout(() => {
      if (!player.alien) {
        return false;
      }
      player.isMoving = false;
      player.animIdle();

      // interactive detection
      const isInteractivePosition =
        this.interactiveElements[`${player.position.x}-${player.position.y}`];
      if (isInteractivePosition) {
        isInteractivePosition.overrunBy(player);
      }

      // follower animations
      if (
        follower &&
        player.alien.anims.currentAnim.key.includes("idle") &&
        follower.isFollowing
      ) {
        // follower.animIdle();
      }

      // leader animations
      if (
        leader ||
        !leader?.isCloseEnough(player.position) ||
        (leader?.isCloseEnough(player.position) &&
          player.alien.anims.currentAnim.key.includes("idle") &&
          leader.isLeading)
      ) {
        leader?.animIdle();
      }

      // next loop
      if (player.moveIntentDir !== "" && player.alien) {
        this.playerLoop();
      }
    }, PLAYER_TICK_TIME);
  }
  // afterPlayerLoop(cb) {
  //   this.afterPlayerLoopCallbacks.push(cb)
  // }
  moveObject(el, pos) {
    const layer = this.scene.children.getByName("layer_1");
    if (!layer) {
      return false;
    }
    const { tilemap: map } = layer;
    const origin = { x: el.position.x, y: el.position.y };
    // assume destination is occupeable
    const destinationElement = this.getObjectAt(pos.x, pos.y);

    const isSwitch =
      [Player.className, NPCharacter.className].includes(
        el.constructor.className
      ) &&
      [Player.className, NPCharacter.className].includes(
        destinationElement.constructor.className
      );

    if (
      (!this.objectIsEmpty(destinationElement) ||
        destinationElement.isClass(Interactive) ||
        destinationElement.isClass(Exit)) &&
      !isSwitch
    ) {
      destinationElement.overrunBy(el);
      el.runningOver(destinationElement);
    }

    if (el.inactive) return;

    // verify not overriding Player when switch with follower
    if (!isSwitch) {
      // clear origin position
      this.setObjectAt(origin.x, origin.y, new Empty(PIXEL_NAME.EMPTY, this));
    }
    // fill destination position
    this.setObjectAt(pos.x, pos.y, el);

    // updates element position
    el.move(pos);
    if (el.isClass(Character) && el.alien) {
      // alien anim
      this.scene.tweens.add({
        targets: el.alien,
        x: {
          value: map.tileToWorldX(pos.x),
          duration: el.isClass(Enemy) ? TICK_TIME : PLAYER_TICK_TIME,
        },
        y: {
          value: map.tileToWorldY(pos.y) + (el.isClass(Player) ? 5 : 0), // offset 5 pixels for player to step a little lower
          duration: el.isClass(Enemy) ? TICK_TIME : PLAYER_TICK_TIME,
        },
      });

      el.animRun();
      // el.alien?.play('brave_run')
    }

    // REMOVE LEADER SO PLAYER CAN INTERACT WITH EXIT
    if (el.isClass(NPCharacter) && el.name === "dwarf") {
      if (
        this.leader.position.x === this.exit.position.x &&
        this.leader.position.y === this.exit.position.y
      ) {
        this.leader.alien?.destroy();
        this.leader.alien = null;
        this.replaceByEmpty(this.leader);
      }
    }

    // if(el.isClass(Jewel) || el.isClass(Mushroom)) {
    //   // alien anim
    //   this.scene.tweens.add({
    //     targets: el.alien,
    //     x: {
    //       value: map.tileToWorldX(pos.x),
    //       duration: TICK_TIME * 0.5,
    //     },
    //     y: {
    //       value: map.tileToWorldY(pos.y),
    //       delay: pos.x !== origin.x ? TICK_TIME * 0.5 : 0,
    //       duration: pos.x !== origin.x ? TICK_TIME * 0.5 : TICK_TIME,
    //     },
    //     // duration: TICK_TIME,
    //   });
    // }
    // if(el.isClass(Barrel)) {
    //   // alien anim
    //   this.scene.tweens.add({
    //     targets: el.alien,
    //     x: {
    //       value: map.tileToWorldX(pos.x),
    //       duration: TICK_TIME * 0.5,
    //     },
    //     y: {
    //       value: map.tileToWorldY(pos.y),
    //       delay: pos.x !== origin.x ? TICK_TIME * 0.5 : 0,
    //       duration: pos.x !== origin.x ? TICK_TIME * 0.5 : TICK_TIME,
    //     },
    //     // duration: TICK_TIME,
    //   });
    // }
    if (el.isClass(Fallable)) {
      // alien anim
      this.scene.tweens.add({
        targets: el.alien,
        x: {
          value: map.tileToWorldX(pos.x),
          duration: pos.y !== origin.y ? TICK_TIME * 0.5 : TICK_TIME,
        },
        y: {
          value: map.tileToWorldY(pos.y),
          delay: pos.x !== origin.x ? TICK_TIME * 0.5 : 0,
          duration: pos.x !== origin.x ? TICK_TIME * 0.5 : TICK_TIME,
        },
        onComplete: () => {
          if (el.inParallelGravity) {
            this.gravityLoop([el]);
          }
        },
        // duration: TICK_TIME,
      });
      // Slide sound and visual for Boulder
      if (pos.x !== origin.x && el.isClass(Boulder)) {
        el.alien.play(
          pos.x > origin.x ? "boulder_roll_right" : "boulder_roll_left"
        );
        if (this.player.pushingDirection !== null) {
          const base = this.getObjectAt(origin.x, origin.y + 1);
          this.scene.events.emit(
            `sounds:rock_slide_${base.isClass(Ground) ? "soft" : "hard"}`
          );
        }
      }
      // Slide sound and visual for Barrel
      if (el.isClass(Barrel)) {
        // is horizontal push and goes to a place with empty bottom neighbour (which means is going to fall)
        // or is falling to sides (falling over a non flat object) in which case x and y are differents
        if (
          (pos.x !== origin.x &&
            this.isDestinationEmpty(pos.x, origin.y + 1)) ||
          (pos.x !== origin.x && pos.y !== origin.y)
        ) {
          setTimeout(
            () =>
              el.alien?.play(
                pos.x > origin.x ? "barrel_roll_right" : "barrel_roll_left"
              ),
            TICK_TIME / 2
          );
        }
        if (pos.x !== origin.x) {
          this.scene.events.emit("sounds:rock_slide_soft");
        }
      }
      // Slide sound and visual for Pillar
      if (el.isClass(Pillar)) {
        // is horizontal push and goes to a place with empty bottom neighbour (which means is going to fall)
        // or is falling to sides (falling over a non flat object) in which case x and y are differents
        if (
          (pos.x !== origin.x &&
            this.isDestinationEmpty(pos.x, origin.y + 1)) ||
          (pos.x !== origin.x && pos.y !== origin.y)
        ) {
          setTimeout(
            () =>
              el.alien?.play(
                pos.x > origin.x ? "pillar_roll_right" : "pillar_roll_left"
              ),
            TICK_TIME / 2
          );
        }
        if (pos.x !== origin.x) {
          this.scene.events.emit("sounds:rock_slide_hard");
        }
      }
      // roll visual for Jewel
      if (pos.x !== origin.x && el.isClass(Jewel)) {
        el.alien.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
          el.alien.play("diamond_idle");
          el.shine.play("diamond_shine");
        });
        el.shine.stop();
        el.alien.play(
          pos.x > origin.x ? "diamond_roll_right" : "diamond_roll_left"
        );
      }
    }

    // this.runScheduledActions()
  }
  pause() {
    this.isPaused = true;
    this.player.moveIntentDir = "";
    this.player.animIdle();
  }
  resume() {
    this.isPaused = false;
    // resume parallel gravity
    this.gravityLoop(this.parallelMovables);
  }
  destroy() {}
  onCharacterDie(el) {
    this.setObjectAt(
      el.position.x,
      el.position.y,
      new Empty(PIXEL_NAME.EMPTY, this)
    );
    this.moveables = this.moveables.filter((e) => e !== el);
    if (el.isClass(Player)) {
      el.moveIntentDir = "";
      // this.player = null
      this.scene.levels.restart();
    }
  }
  onJewelPick(el) {
    this.setObjectAt(
      el.position.x,
      el.position.y,
      new Empty(PIXEL_NAME.EMPTY, this)
    );
    this.moveables = this.moveables.filter((e) => e !== el);
    this.collectedDiamonds++;
    this.scene.events.emit("score:diamond_set", this.collectedDiamonds);
    if (this.diamondsGoal > 0 && this.diamondsGoal <= this.collectedDiamonds) {
      this.destroyBlock();
      // UI update
      this.scene.events.emit("score:diamond_goal");
      this.scene.events.emit("sounds:coin");
    }
  }
  destroyBlock() {
    // on goal reached
    // remove exit block
    const exit = Object.values(this.interactiveElements).find((el) =>
      el?.isClass(Exit)
    );
    if (exit?.block) {
      this.onDestroyElement(exit.block);
      exit.block.alien.destroy();
    }
  }
  onMushroomPick(el) {
    this.setObjectAt(
      el.position.x,
      el.position.y,
      new Empty(PIXEL_NAME.EMPTY, this)
    );
    this.moveables = this.moveables.filter((e) => e !== el);
    this.collectedShrooms = +1;
  }
  onEnemyKill(el) {
    this.killedEnemies++;
  }
  onDestroyElement(el) {
    this.setObjectAt(
      el.position.x,
      el.position.y,
      new Empty(PIXEL_NAME.EMPTY, this)
    );
    this.moveables = this.moveables.filter((e) => e !== el);
    this.parallelMovables = this.parallelMovables.filter((e) => e !== el);
  }
  replaceByEmpty(el) {
    const emptyPlace = new Empty(PIXEL_NAME.EMPTY, this);
    emptyPlace.setPosition(el.position);
    const objectInDestination = this.getObjectAt(el.position.x, el.position.y);
    // validation to prevent emptying another object that take place before this method is called.
    // happens in case of barrels exploding by fallable. Barrel has a half tick delay from overrun to explode.
    // that half tick offset makes the place in the grid to get filled with the overruner object
    if (objectInDestination === el) {
      this.setObjectAt(el.position.x, el.position.y, emptyPlace);
      // this.moveables = this.moveables.filter(e => e === el)
    }
  }
  onExplode(el) {
    // moved into Barrel class
  }
  cleanLevel() {
    this.scene.events.off("player_moves");
    this.scene.events.off("dialogue:hidden");
    this.scene.events.off("dialogue:message_shown");
    this.objects.forEach((row) =>
      row.forEach((el) => {
        if (el.alien) {
          el.alien.destroy();
          el.alien = null;
        }
      })
    );
  }
  async onComplete() {
    this.pause();

    const levelData = await dbManager.get(this.constructor.key);

    if (levelData) {
      this.prevBest = levelData?.comp;
    }
    const comp = this.scene.rules.calcCompletion(
      this.countDiamonds,
      this.collectedDiamonds,
      this.countEnemies,
      this.killedEnemies,
      this.countShrooms,
      Math.max(this.collectedShrooms, 0)
    );
    const newLevelData = {
      countDiamonds: this.countDiamonds,
      collectedDiamonds: this.collectedDiamonds,
      countEnemies: this.countEnemies,
      killedEnemies: this.killedEnemies,
      countShrooms: this.countShrooms,
      collectedShrooms: Math.max(this.collectedShrooms, 0),
      comp,
      prevBest: this.prevBest,
    };
    if (!levelData || (levelData && newLevelData.comp >= levelData.comp)) {
      await dbManager.set(this.constructor.key, newLevelData);
    }
    // updates global shroom counter
    await dbManager.set(
      "totalShroomsAmount",
      this.totalShroomsAmount + this.collectedShrooms
    );
    // updates level shroom counts even if is not the best completion score
    const storedData = await dbManager.get(this.constructor.key);
    // in case of shroom given the counter could be lower than 0
    storedData.collectedShrooms = Math.max(0, this.collectedShrooms);
    await dbManager.set(this.constructor.key, storedData);

    this.scene.events.emit("levelcompleted", newLevelData);
  }
  onInteractWith(el) {
    if (el.ephemeral) {
      this.interactiveElements[`${el.position.x}-${el.position.y}`] = null;
    }
  }
  onAddElement(el) {
    console.warn(`implement element added ${this.id}`);
  }
  getConfigAt(x, y) {
    return this.isDestinationInBounds(x, y) && this.config[y][x];
  }
  getCoords(id) {
    const found = [];
    for (let i = 0; i < this.config.length; i++) {
      for (let j = 0; j < this.config[i].length; j++) {
        if (this.config[i][j] === id) {
          found.push({ x: j, y: i });
        }
      }
    }
    return found;
  }
  getObjectAt(x, y) {
    return this.isDestinationInBounds(x, y) && this.objects[y][x];
  }
  setObjectAt(x, y, obj) {
    this.objects[y][x] = obj;
  }
  isPositionClear(x, y) {
    return this.getObjectAt(x, y).isEmpty;
  }
  isDestinationInBounds(x, y) {
    const xInBounds = x < this.config[0].length && x >= 0;
    const yInBounds = y < this.config.length && y >= 0;
    return xInBounds && yInBounds;
  }
  objectIsEmpty(el) {
    return el && el.isClass(CLASS_NAMES.EMPTY);
  }
  isDestinationEmpty(x, y) {
    return this.getObjectAt(x, y).isEmpty;
  }

  async playEarthquake(range, intensity = 3) {
    await this.shakeCamera(intensity);
    const probability =
      intensity === 4
        ? 1
        : intensity === 3
        ? 0.75
        : intensity === 2
        ? 0.5
        : 0.25;
    this.destroyObjectsInRange(range, probability);
  }

  destroyObjectsInRange(range, probability, condition) {
    for (let y = range.y1; y < range.y2; y++) {
      for (let x = range.x1; x <= range.x2; x++) {
        // remove interactive in zone
        if (this.interactiveElements[`${x}-${y}`]) {
          this.interactiveElements[`${x}-${y}`] = null;
        }
        const toDestroy = this.getObjectAt(x, y);
        const strengthEnough = Math.random() < probability;
        const destroyableClass =
          toDestroy.isClass(Ground) || toDestroy.isClass(Debris);
        const destroyByCondition = condition && condition(toDestroy);
        if (
          (strengthEnough && toDestroy && destroyableClass) ||
          destroyByCondition
        ) {
          toDestroy.overrunBy();
          this.onDestroyElement(toDestroy);
        }
      }
    }
  }

  shakeCamera(intensity) {
    const camera = this.scene.cameras.main;
    this.fx = this.scene.sound.add("earthquake");
    const length = 2500;
    const strength = 0.005 * intensity;
    camera.shake(length, strength);
    this.fx.play();

    return new Promise((res) => {
      setTimeout(res, length);
    });
  }

  updatePositionsOnScale() {
    const layer = this.scene.children.getByName("layer_1");
    const { tilemap: map } = layer;
    this.moveables.forEach((mv) => {
      if (mv.alien) {
        mv.alien.setPosition(
          map.tileToWorldX(mv.position.x),
          map.tileToWorldY(mv.position.y)
        );
      }
    });
    // fix angel position
    const exit = Object.values(this.interactiveElements).find((el) =>
      el?.isClass(Exit)
    );
    exit.block?.alien.setPosition(
      map.tileToWorldX(exit.position.x - 2),
      map.tileToWorldY(exit.position.y)
    );

    Object.values(this.decorationElements).forEach((el) => {
      el.alien.x = UI.tileToScreen(el.position.x);
      el.alien.y = UI.tileToScreen(el.position.y);
    });
  }
  setTickTimeout(callback, ticks) {
    let tickDelayToTrigger = ticks;
    const onTick = (lvl) => {
      if (tickDelayToTrigger-- === 0) {
        callback(lvl);
      } else {
        lvl.onNextTick((level) => onTick(level));
      }
    };
    onTick(this);
  }
  // awaits game loops equals to the given param. It depends on game loops, it wont't work with paused game.
  awaitTicks(amountOfTicks) {
    return new Promise((res) => this.setTickTimeout(res, amountOfTicks));
  }
  // awaits a time equals to the given param by TICK_TIME. It not depends on real game loops, it works with paused game.
  awaitTicksTime(amountOfTicks) {
    return new Promise((res) => {
      setTimeout(res, TICK_TIME * amountOfTicks);
    });
  }
  async teleportObject(el, position) {
    el.alien.alpha = 0;
    this.moveObject(el, position);
    el.setPosition(position);
    await this.awaitTicks(3);
    el.alien.alpha = 1;
  }

  // factory
  createBoulderAt(pos) {
    const boulder = this.scene.add
      .sprite(48, 16)
      .setOrigin(0, 0.25)
      .play("boulder_idle");
    boulder.setScale(SCALE());
    boulder.x = UI.tileToScreen(pos.x);
    boulder.y = UI.tileToScreen(pos.y);

    const element = new Boulder(PIXEL_NAME.BOULDER, this);
    element.alien = boulder;
    return element;
  }
  createBarrelAt(pos) {
    const barrel = this.scene.add
      .sprite(48, 16)
      .setOrigin(0.05, 0.2)
      .play("barrel_idle");
    barrel.setScale(SCALE());

    barrel.x = UI.tileToScreen(pos.x);
    barrel.y = UI.tileToScreen(pos.y);

    const element = new Barrel(PIXEL_NAME.BARREL, this);
    element.alien = barrel;

    return element;
  }
  createEnemyAt(pos) {
    const enemy = this.scene.add
      .sprite(48, 16)
      .setOrigin(0.125, 0.125)
      .play("bat_move");
    enemy.setScale(SCALE());
    enemy.x = UI.tileToScreen(pos.x);
    enemy.y = UI.tileToScreen(pos.y);

    const element = new Enemy(PIXEL_NAME.ENEMY, this);
    element.alien = enemy;
    element.setPosition(pos);

    return element;
  }
  destroyObjectsByKind(kind) {
    const destructionForce = new Explosion(PIXEL_NAME.EXPLOSION, this);
    this.objects.forEach((row) =>
      row.forEach((el) => {
        if (el.isClass(kind)) {
          // const toDestroy = this.getObjectAt(x, y);
          el.overrunBy(destructionForce);
          this.onDestroyElement(el);
        }
      })
    );
  }
  destroy() {
    this.queuedActions = [];
  }
}

export default Level;
